import { useEffect, useMemo, useState } from 'react';
import { useGameStore } from '../../core/store';
import { skillBoards } from '../../data/skillBoards';
import './next-skill-card.css';

const HEADINGS: Record<string, string> = {
  ai: 'AI & LLM',
  cloud: 'CLOUD',
  frontend: 'FRONTEND',
  ml: 'ML',
  three: '3D / R3F',
  backend: 'BACKEND',
  database: 'DATA',
  architecture: 'SYSTEMS',
};

function LockIcon() {
  return (
    <svg viewBox="0 0 14 14" className="next-skill__lock" fill="none" aria-hidden>
      <rect
        x="3"
        y="6"
        width="8"
        height="6.2"
        rx="1.2"
        stroke="currentColor"
        strokeWidth="1.2"
      />
      <path
        d="M4.8 6V4.4a2.2 2.2 0 0 1 4.4 0V6"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function BrainIcon() {
  return (
    <svg viewBox="0 0 64 64" className="next-skill__brain" fill="none" aria-hidden>
      <path
        d="M28 12c-7 0-12 5-12 12 0 2-.5 4-1.5 5.5C12 32 10 36 12 41c2 5 7 8 13 8h2"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
      <path
        d="M36 12c7 0 12 5 12 12 0 2 .5 4 1.5 5.5C52 32 54 36 52 41c-2 5-7 8-13 8h-2"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
      <path
        d="M32 12v37M22 22h8M34 22h8M20 32h12M34 32h10M22 41h10M34 41h8"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        opacity="0.85"
      />
      <circle cx="24" cy="26" r="1.4" fill="currentColor" />
      <circle cx="40" cy="26" r="1.4" fill="currentColor" />
      <circle cx="28" cy="36" r="1.3" fill="currentColor" />
      <circle cx="38" cy="36" r="1.3" fill="currentColor" />
    </svg>
  );
}

function headingForBoard(id: string, title: string, subtitle: string) {
  return (
    HEADINGS[id] ??
    `${title} ${subtitle}`.replace(/\s+/g, ' ').trim().toUpperCase()
  );
}

export function NextSkillCard({ compact = false }: { compact?: boolean }) {
  const unlocked = useGameStore((s) => s.unlockedSkills);
  const toast = useGameStore((s) => s.careerToast);
  const toastAt = useGameStore((s) => s.careerToastAt);
  const [celebrate, setCelebrate] = useState(false);
  const [celebrateHeading, setCelebrateHeading] = useState<string | null>(null);

  const unlockedSet = useMemo(() => new Set(unlocked), [unlocked]);

  // Path bar "CURRENT" = first locked board. Top card shows that same target.
  const upcoming = useMemo(() => {
    return skillBoards.find((b) => !unlockedSet.has(b.id)) ?? null;
  }, [unlockedSet]);

  const allCleared = useMemo(
    () => skillBoards.every((b) => unlockedSet.has(b.id)),
    [unlockedSet],
  );

  useEffect(() => {
    if (!toast || !toastAt) return;
    const board =
      skillBoards.find((b) => {
        const label = `${b.title} ${b.subtitle}`.replace(/\s+/g, ' ').trim();
        return label.toLowerCase() === toast.toLowerCase();
      }) ?? skillBoards.filter((b) => unlockedSet.has(b.id)).at(-1);

    setCelebrate(true);
    setCelebrateHeading(
      board
        ? headingForBoard(board.id, board.title, board.subtitle)
        : toast.toUpperCase(),
    );
    const id = window.setTimeout(() => {
      setCelebrate(false);
      setCelebrateHeading(null);
    }, 2200);
    return () => window.clearTimeout(id);
  }, [toast, toastAt, unlockedSet]);

  const complete = allCleared && !celebrate;

  const heading = celebrate
    ? celebrateHeading ?? 'SKILL UNLOCKED'
    : complete
      ? 'PATH COMPLETE'
      : upcoming
        ? headingForBoard(upcoming.id, upcoming.title, upcoming.subtitle)
        : 'EXIT AHEAD';

  return (
    <div
      className={[
        'next-skill',
        compact ? 'next-skill--compact' : '',
        celebrate ? 'is-unlocked' : '',
        complete ? 'is-complete' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      role="status"
      aria-live="polite"
    >
      <div className="next-skill__copy">
        <span className="next-skill__eyebrow">
          {celebrate
            ? 'Skill unlocked'
            : complete
              ? 'All clear'
              : upcoming
                ? 'Collect next'
                : 'Exit ahead'}
        </span>
        <strong className="next-skill__heading">{heading}</strong>
        <span className="next-skill__status">
          {celebrate ? (
            'CLEARED'
          ) : complete ? (
            'MASTERED'
          ) : upcoming ? (
            <>
              <LockIcon /> WALK TO UNLOCK
            </>
          ) : (
            'GO'
          )}
        </span>
      </div>
      <div className="next-skill__art" aria-hidden>
        <BrainIcon />
      </div>
    </div>
  );
}
